import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonSpinner,
    IonText,
    IonButton,
    useIonToast
} from '@ionic/react';
import { ApiClient } from '../api/apiClient';
import { useSubscription } from '../components/auth/contexts/SubscriptionContext';
import { useAuth } from '../components/auth/contexts/AuthContext';

interface ApiError {
    response?: {
        data?: {
            error?: string;
        };
    };
}

export const VerifySubscription: React.FC = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const history = useHistory();
    const location = useLocation();
    const [present] = useIonToast();
    const { refreshSubscriptionState } = useSubscription();
    const { currentUser } = useAuth();

    useEffect(() => {
        const verifySession = async () => {
            // Get session_id from URL query parameters
            const params = new URLSearchParams(location.search);
            const sessionId = params.get('session_id');

            if (!sessionId) {
                setError('No session ID found');
                setIsLoading(false);
                return;
            }

            try {
                const apiClient = ApiClient.getInstance();
                const response = await apiClient.get(`/subscriptions/verify-session/${sessionId}`);
                
                // Force refresh the Firebase token to get latest claims
                if (currentUser) {
                    await currentUser.getIdToken(true);
                }

                // Update subscription state in context
                await refreshSubscriptionState();

                setMessage(response.data.message);
                
                // Show success toast
                present({
                    message: response.data.message,
                    duration: 3000,
                    position: 'bottom',
                    color: 'success'
                });

                // Redirect after a short delay
                setTimeout(() => {
                    history.replace('/');
                }, 2000);
            } catch (err) {
                const apiError = err as ApiError;
                const errorMessage = apiError.response?.data?.error || 'Failed to verify subscription';
                setError(errorMessage);
                
                // Show error toast
                present({
                    message: errorMessage,
                    duration: 3000,
                    position: 'bottom',
                    color: 'danger'
                });

                // Redirect to subscription page after error
                setTimeout(() => {
                    history.replace('/subscription');
                }, 2000);
            } finally {
                setIsLoading(false);
            }
        };

        verifySession();
    }, [location, history, present, refreshSubscriptionState, currentUser]);

    return (
        <IonPage>
            <IonContent className="ion-padding">
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '100%' 
                }}>
                    <IonCard style={{ width: '100%', maxWidth: '400px' }}>
                        <IonCardHeader>
                            <IonCardTitle className="ion-text-center">
                                {isLoading ? 'Verifying Subscription...' : 
                                 error ? 'Subscription Error' : 
                                 'Subscription Updated'}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            {isLoading ? (
                                <div className="ion-text-center ion-padding">
                                    <IonSpinner />
                                    <IonText>
                                        <p>Please wait while we verify your subscription...</p>
                                    </IonText>
                                </div>
                            ) : error ? (
                                <div className="ion-text-center">
                                    <IonText color="danger">
                                        <p>{error}</p>
                                    </IonText>
                                    <IonButton expand="block" onClick={() => history.replace('/subscription')}>
                                        Try Again
                                    </IonButton>
                                </div>
                            ) : (
                                <div className="ion-text-center">
                                    <IonText>
                                        <p>{message}</p>
                                        <p>Redirecting to dashboard...</p>
                                    </IonText>
                                </div>
                            )}
                        </IonCardContent>
                    </IonCard>
                </div>
            </IonContent>
        </IonPage>
    );
}; 